import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { WhatsAppButton } from "@/components/layout/WhatsAppButton";
import { SEOHead } from "@/components/seo/SEOHead";
import { ProductListStructuredData } from "@/components/seo/StructuredData";
import { Button } from "@/components/ui/button";
import { ProductImageDisplay } from "@/components/product/ProductImageDisplay";
import { useRef, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageCircle, Filter, ShoppingBag, Plus, Loader2 } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { staticAllProducts, type StaticProduct } from "@/data/staticProducts";

// Placeholder image for products without images
import placeholderImg from "@/assets/products/lineup1.png";

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  concern: string | null;
  description: string | null;
  image_url: string | null;
  in_stock: boolean;
  subcategory: string | null;
}

const categories = [
  "All",
  "Cleanser",
  "Serum",
  "Moisturizer",
  "Sunscreen",
  "Body",
  "Toner",
  "Lip Care",
  "Face Care",
  "Others",
];

// iOS detection for disabling animations that cause Safari compositor glitches
const isIOS = typeof navigator !== 'undefined' && 
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || 
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

function getIsPortrait() {
  if (typeof window === "undefined") return true;
  // Prefer matchMedia (more reliable than innerWidth/innerHeight on iOS Safari)
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(orientation: portrait)").matches;
  }
  return window.innerHeight >= window.innerWidth;
}

// Flag to completely disable Framer Motion on iOS portrait (prevents Safari zoom bug)
const useIOSPortraitMode = () => {
  const [isPortrait, setIsPortrait] = useState(getIsPortrait);
  
  useEffect(() => {
    const update = () => setIsPortrait(getIsPortrait());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  
  return isIOS && isPortrait;
};

interface ProductCardProps {
  product: Product;
  placeholderImg: string;
  formatPrice: (kobo: number) => string;
  handleAddToCart: (product: Product) => void;
}

// Static ProductCard - NO Framer Motion, NO animations
const ProductCard: React.FC<ProductCardProps> = ({
  product,
  placeholderImg,
  formatPrice,
  handleAddToCart,
}) => (
  <div className="group">
    <Link
      to={`/products/${product.id}`}
      className="bg-background rounded-2xl overflow-hidden shadow-card h-full flex flex-col block"
    >
      {/* Product Image with Decorative Display */}
      <div className="aspect-square relative overflow-visible p-3">
        <ProductImageDisplay
          imageUrl={product.image_url || placeholderImg}
          alt={product.name}
          showBadge={true}
          badgeText={product.subcategory || product.category}
        />
        {/* Hover Overlay - disabled on touch devices via CSS */}
        <div className="absolute inset-3 z-20 rounded-2xl bg-primary/30 opacity-0 group-hover:opacity-100 hidden md:flex items-center justify-center md:backdrop-blur-sm gap-2">
          <Button
            variant="hero"
            size="default"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddToCart(product);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add to Cart
          </Button>
        </div>
        {/* Concern Badge */}
        {product.concern && (
          <span className="absolute top-6 right-6 z-30 px-3 py-1 bg-primary/90 rounded-full text-xs font-medium text-primary-foreground">
            {product.concern}
          </span>
        )}
      </div>
      {/* Product Info */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-medium text-foreground mb-2 group-hover:text-primary">
          {product.name}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 flex-1 line-clamp-2">
          {product.description || "Premium skincare product for your daily routine."}
        </p>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xl font-semibold text-primary">
            {formatPrice(product.price)}
          </span>
          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddToCart(product);
              }}
            >
              <ShoppingBag className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              asChild
              onClick={(e) => e.stopPropagation()}
            >
              <a
                href={`https://api.whatsapp.com/send?phone=2347065732481&text=Hi! I'd like to know more about ${product.name}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </Link>
  </div>
);

const Products = () => {
  const ref = useRef(null);
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category");
  const [activeCategory, setActiveCategory] = useState(categoryFromUrl || "All");
  // Initialize with static products for immediate rendering (SEO)
  const [products, setProducts] = useState<Product[]>(staticAllProducts as Product[]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetchedLive, setHasFetchedLive] = useState(false);
  const { addItem } = useCart();
  const { toast } = useToast();
  
  // Detect iOS portrait mode - completely removes all animations when true
  const iosPortrait = useIOSPortraitMode();

  // CRITICAL: iOS zoom prevention - runs immediately when component mounts
  useEffect(() => {
    // Only run on iOS
    if (!isIOS) return;

    let lastTouchEnd = 0;
    
    const preventGestureStart = (e: Event) => {
      e.preventDefault();
      if (document.body.style.zoom !== '1') {
        document.body.style.zoom = '1';
      }
    };
    
    const preventGestureChange = (e: Event) => {
      e.preventDefault();
      if (document.body.style.zoom !== '1') {
        document.body.style.zoom = '1';
      }
    };
    
    const preventGestureEnd = (e: Event) => {
      e.preventDefault();
      if (document.body.style.zoom !== '1') {
        document.body.style.zoom = '1';
      }
    };
    
    const preventDoubleTap = (event: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };
    
    const preventTouchZoom = (event: TouchEvent) => {
      if ((event as any).scale && (event as any).scale !== 1) {
        event.preventDefault();
      }
    };
    
    // Add all event listeners
    document.addEventListener('gesturestart', preventGestureStart, { passive: false });
    document.addEventListener('gesturechange', preventGestureChange, { passive: false });
    document.addEventListener('gestureend', preventGestureEnd, { passive: false });
    document.addEventListener('touchend', preventDoubleTap, false);
    document.addEventListener('touchmove', preventTouchZoom, { passive: false });
    
    // Force zoom level constantly
    const zoomInterval = setInterval(() => {
      if (document.body.style.zoom !== '1') {
        document.body.style.zoom = '1';
      }
      // Also check viewport zoom
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
    }, 100);
    
    // Cleanup
    return () => {
      document.removeEventListener('gesturestart', preventGestureStart);
      document.removeEventListener('gesturechange', preventGestureChange);
      document.removeEventListener('gestureend', preventGestureEnd);
      document.removeEventListener('touchend', preventDoubleTap);
      document.removeEventListener('touchmove', preventTouchZoom);
      clearInterval(zoomInterval);
    };
  }, []);

  // Update active category when URL param changes
  useEffect(() => {
    if (categoryFromUrl && categories.includes(categoryFromUrl)) {
      setActiveCategory(categoryFromUrl);
    }
  }, [categoryFromUrl]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data, error } = await supabase
          .from("products")
          .select("*")
          .eq("in_stock", true)
          .eq("is_active", true)
          .order("name", { ascending: true });

        if (error) throw error;
        // Replace static data with fresh data
        if (data && data.length > 0) {
          setProducts(data);
        }
        setHasFetchedLive(true);
      } catch (error) {
        console.error("Error fetching products:", error);
        // Keep static products as fallback
        toast({
          title: "Note",
          description: "Showing cached products. Pull to refresh for latest.",
          variant: "default",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [toast]);

  const filteredProducts = activeCategory === "All"
    ? products
    : products.filter((p) => p.category === activeCategory);

  const formatPrice = (kobo: number) => {
    return `₦${(kobo / 100).toLocaleString()}`;
  };

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url || placeholderImg,
      category: product.category,
    });
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  return (
    <div className="products-page-wrapper min-h-screen-safe touch-manipulation" style={{ paddingBottom: "calc(8rem + env(safe-area-inset-bottom, 0px))" }}>
      <SEOHead
        title="Shop Skincare - CeraVe & More Lagos"
        description="Browse our collection of authentic skincare products from trusted global brands. CeraVe, The Ordinary, and more. Free shipping in Lagos."
        keywords="skincare products Lagos, CeraVe Nigeria, authentic skincare, moisturizer, serum, sunscreen Nigeria, buy skincare Lagos"
      />
      {/* Structured data for product collection - visible to crawlers */}
      <ProductListStructuredData products={products} />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-primary">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Plain div - no Framer Motion */}
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary mb-4">
                Shop
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
                Our <span className="text-gradient">Products</span>
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Authentic skincare products carefully curated for your unique skin journey. 
                Every product is 100% genuine and sourced from authorized distributors.{" "}
                <Link to="/testimonials" className="text-primary hover:underline">
                  Read customer reviews
                </Link>{" "}
                or{" "}
                <Link to="/contact" className="text-primary hover:underline">
                  get personalized recommendations
                </Link>. Looking for the perfect gift?{" "}
                <Link to="/gift-cards" className="text-primary hover:underline">
                  Browse our skincare gift cards
                </Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section ref={ref} className="section-padding bg-card">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Filter - plain div */}
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="h-5 w-5 text-primary" />
                <span className="font-medium text-foreground">Filter by category</span>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3 pb-2 scrollbar-hide">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-4 py-2 sm:px-5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                      activeCategory === category
                        ? "bg-gradient-rose text-primary-foreground shadow-glow"
                        : "bg-muted text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Loading State - only show spinner if we have no products yet */}
            {isLoading && products.length === 0 ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">
                  No products found in this category.
                </p>
              </div>
            ) : (
              /* Products Grid - Group by subcategory for Others */
              activeCategory === "Others" ? (
                <div className="space-y-10">
                  {(() => {
                    const subcategories = [...new Set(filteredProducts.map(p => p.subcategory).filter(Boolean))];
                    return subcategories.map((subcategory) => (
                      <div key={subcategory}>
                        <h3 className="text-xl font-serif text-foreground mb-4 border-b border-border pb-2">
                          {subcategory}
                        </h3>
                        <div className="product-grid-container grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full max-w-full">
                          {filteredProducts
                            .filter(p => p.subcategory === subcategory)
                            .map((product) => (
                              <ProductCard
                                key={product.id}
                                product={product}
                                placeholderImg={placeholderImg}
                                formatPrice={formatPrice}
                                handleAddToCart={handleAddToCart}
                              />
                            ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
              <div className="product-grid-container grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full max-w-full">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      placeholderImg={placeholderImg}
                      formatPrice={formatPrice}
                      handleAddToCart={handleAddToCart}
                    />
                  ))}
                </div>
              )
            )}

            {/* Purchase Note - plain div */}
            <div className="mt-16 text-center">
              <div className="inline-block bg-blush rounded-2xl p-8 max-w-2xl">
                <h3 className="font-serif text-xl text-foreground mb-4">
                  Ready to Purchase?
                </h3>
                <p className="text-muted-foreground mb-6">
                  Add products to your cart and checkout with Paystack for secure online payment.
                  Or send us a message on WhatsApp for personalized assistance.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button variant="hero" size="lg" asChild>
                    <a href="/checkout" className="flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5" />
                      View Cart & Checkout
                    </a>
                  </Button>
                  <Button variant="whatsapp" size="lg" asChild>
                    <a
                      href="https://api.whatsapp.com/send?phone=2347065732481&text=Hi! I'd like to place an order."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <MessageCircle className="h-5 w-5" />
                      Shop on WhatsApp
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton iosPortrait={iosPortrait} />
    </div>
  );
};

export default Products;